import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRatings, postRating, type PostRatingInput, type RatingsBulkResponse } from '@/api/ratings';
import { tripKeys } from '@/api/queryKeys';

/**
 * GET /api/ratings bulk fetch (Aggregate pro Act + meine Ratings) plus
 * POST-Mutation. Optimistic-Update auf den Bulk-Cache hält die Sterne
 * sofort sichtbar; das Server-Aggregat (avg/count) refresht im
 * onSuccess-Invalidate.
 *
 * Auch RecapPage/Booklet refreshen ihre Caches, weil deren Top-Acts
 * `avgStars`/`ratingsCount` aus den Ratings ableiten.
 */
export function useRatings(slug: string) {
  const qc = useQueryClient();

  const query = useQuery<RatingsBulkResponse>({
    queryKey: tripKeys.ratings(slug),
    queryFn: ({ signal }) => fetchRatings(slug, signal),
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (input: PostRatingInput) => postRating(slug, input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: tripKeys.ratings(slug) });
      const prev = qc.getQueryData<RatingsBulkResponse>(tripKeys.ratings(slug));
      if (prev) {
        const oldStars = prev.mine[input.actId]?.stars ?? 0;
        const oldAgg = prev.byAct[input.actId] ?? { avg: 0, count: 0 };
        // Rough optimistic estimate — Server invalidiert exakt nach.
        let nextCount = oldAgg.count;
        let nextSum = oldAgg.avg * oldAgg.count;
        if (oldStars === 0) {
          nextCount += 1;
          nextSum += input.stars;
        } else {
          nextSum += (input.stars - oldStars);
        }
        const nextAvg = nextCount ? Math.round((nextSum / nextCount) * 10) / 10 : 0;
        qc.setQueryData<RatingsBulkResponse>(tripKeys.ratings(slug), {
          ...prev,
          byAct: { ...prev.byAct, [input.actId]: { avg: nextAvg, count: nextCount } },
          mine: { ...prev.mine, [input.actId]: { stars: input.stars, comment: input.comment ?? '', updatedAt: new Date().toISOString() } },
        });
      }
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(tripKeys.ratings(slug), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: tripKeys.ratings(slug) });
      qc.invalidateQueries({ queryKey: tripKeys.recap(slug) });
      qc.invalidateQueries({ queryKey: tripKeys.booklet(slug) });
    },
  });

  return { query, mutation };
}
